import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider, useMsal } from "@azure/msal-react";
import MsalAuthenticationWrapper from "auth/components/MsalAuthenticationWrapper";
import { authMode, authRedirectUri } from "auth/utils/authSettings";
import { getAccessRequest } from "auth/utils/getAccessRequest";
import getQueryParamTenantId from "auth/utils/getQueryParamTenantId";
import { getSignedInAccount } from "auth/utils/getSignedInAccount";
import { env } from "env";
import React, { PropsWithChildren } from "react";

const isB2CAuthEnabled = authMode === "b2c";

const config = {
  auth: {
    // To understand the exclamation mark refer to ->
    // https://stackoverflow.com/questions/54496398/typescript-type-string-undefined-is-not-assignable-to-type-string
    clientId: isB2CAuthEnabled
      ? env.REACT_APP_AUTH_B2C_CLIENT_ID!
      : env.REACT_APP_AUTH_CLIENT_ID!,
    redirectUri: authRedirectUri,
    authority: isB2CAuthEnabled
      ? `https://${env.REACT_APP_AUTH_B2C_DOMAIN}/${env.REACT_APP_AUTH_B2C_TENANT}/B2C_1A_SIGNUP_SIGNIN`
      : "https://login.microsoftonline.com/organizations/",
    knownAuthorities: isB2CAuthEnabled ? [env.REACT_APP_AUTH_B2C_DOMAIN!] : [],
  },
  cache: {
    // To understand the as "localStorage" refer to ->
    // https://github.com/syncweek-react-aad/react-aad/issues/95
    cacheLocation: "localStorage",
    // storeAuthStateInCookie: true,
  },
  // system: {
  //   loggerOptions: {
  //     loggerCallback: (
  //       level: LogLevel,
  //       message: string,
  //       containsPii: boolean
  //     ): void => {
  //       // if (containsPii) {
  //       //   return;
  //       // }
  //       switch (level) {
  //         case LogLevel.Error:
  //           console.error(message);
  //           return;
  //         case LogLevel.Info:
  //           // eslint-disable-next-line no-console
  //           console.info(message);
  //           return;
  //         case LogLevel.Verbose:
  //           // eslint-disable-next-line no-console
  //           console.debug(message);
  //           return;
  //         case LogLevel.Warning:
  //           // eslint-disable-next-line no-console
  //           console.warn(message);
  //           return;
  //       }
  //     },
  //     piiLoggingEnabled: true,
  //   },
  // },
};

const pca =
  env.NODE_ENV === "test" ? undefined : new PublicClientApplication(config);

const getExtraQueryParams = () => {
  const tenantId = getQueryParamTenantId();

  if (!tenantId) return undefined;

  return {
    tenant_hint: tenantId,
  };
};

export const getSilentAccessToken = async () => {
  const request = getAccessRequest(pca);

  if (!request || !pca) return null;

  let accessToken: string | null = null;
  try {
    const extraQueryParameters = getExtraQueryParams();
    if (isB2CAuthEnabled || !extraQueryParameters) {
      accessToken = await (
        await pca.acquireTokenSilent({ ...request, extraQueryParameters })
      ).accessToken;
    } else {
      const authResult = await pca.acquireTokenSilent({
        ...request,
        extraQueryParameters,
      });

      if (authResult.account?.idTokenClaims) {
        const currentTenant = authResult.account.idTokenClaims.rpaf_tenant;
        if (currentTenant === extraQueryParameters.tenant_hint)
          accessToken = authResult.accessToken;
      }
    }
  } catch (error) {
    console.error(error);
  }

  return accessToken;
};

export const getCurrentPublicToken = () => {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get("token");
};

const AccountSelector: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const { instance } = useMsal();

  const account = getSignedInAccount(instance);
  instance.getActiveAccount() == null &&
    !!account &&
    instance.setActiveAccount(account);

  return <>{children}</>;
};

const Authentication: React.FC<PropsWithChildren> = ({ children }) => {
  if (!pca) return <>{children}</>;

  return (
    <MsalProvider instance={pca}>
      <AccountSelector>
        <MsalAuthenticationWrapper>{children}</MsalAuthenticationWrapper>
      </AccountSelector>
    </MsalProvider>
  );
};

export default Authentication;
